import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  Output,
  inject,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';

export interface SandboxDashboardDropdownOption {
  value: string | number;
  label: string;
  /** Muted text after the label in the list only, e.g. "results/page". */
  suffix?: string;
}

type OptionValue = SandboxDashboardDropdownOption['value'];

/**
 * Dropdown used inside the sandbox dashboard.
 * - Single mode: `value` / `valueChange`, trigger shows "prefix value".
 * - Multiple mode: `values` / `valuesChange`, trigger shows the selection as pills.
 * Styled from the parent dashboard's --sbd-* theme variables.
 */
@Component({
  selector: 'lib-sandbox-dashboard-dropdown',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './sandbox-dashboard-dropdown.component.html',
  styleUrl: './sandbox-dashboard-dropdown.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SandboxDashboardDropdownComponent {
  @Input({ required: true }) options: SandboxDashboardDropdownOption[] = [];
  @Input() value: OptionValue | null = null;
  @Input() multiple = false;
  @Input() values: OptionValue[] = [];
  /** Multiple mode: options can't be unticked below this count. */
  @Input() minSelected = 0;
  /** Text before the selected value in the trigger, e.g. "Showing:". */
  @Input() prefix = '';
  @Input() placeholder = 'Select';
  @Input() ariaLabel = '';
  @Input() align: 'left' | 'right' = 'left';
  /** Open the list above the trigger (for controls near the bottom of a panel). */
  @Input() openUp = false;
  @Input() disabled = false;
  @Output() valueChange = new EventEmitter<OptionValue>();
  @Output() valuesChange = new EventEmitter<OptionValue[]>();

  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  readonly isOpen = signal(false);

  get selectedLabel(): string {
    return this.options.find((o) => o.value === this.value)?.label ?? '';
  }

  get selectedOptions(): SandboxDashboardDropdownOption[] {
    return this.options.filter((o) => this.values.includes(o.value));
  }

  get accessibleSummary(): string {
    const text = this.multiple
      ? this.selectedOptions.map((o) => o.label).join(', ')
      : this.selectedLabel;
    return this.ariaLabel
      ? `${this.ariaLabel}: ${text || this.placeholder}`
      : '';
  }

  isSelected(option: SandboxDashboardDropdownOption): boolean {
    return this.multiple
      ? this.values.includes(option.value)
      : option.value === this.value;
  }

  isLocked(option: SandboxDashboardDropdownOption): boolean {
    return (
      this.multiple &&
      this.isSelected(option) &&
      this.values.length <= this.minSelected
    );
  }

  toggle(): void {
    if (this.disabled) return;
    if (this.isOpen()) {
      this.close();
    } else {
      this.isOpen.set(true);
      const first = Math.max(
        0,
        this.options.findIndex((o) => this.isSelected(o)),
      );
      setTimeout(() => this.focusOption(first));
    }
  }

  select(option: SandboxDashboardDropdownOption): void {
    if (this.multiple) {
      if (this.isLocked(option)) return;
      const next = this.isSelected(option)
        ? this.values.filter((v) => v !== option.value)
        : this.options
            .filter(
              (o) => o.value === option.value || this.values.includes(o.value),
            )
            .map((o) => o.value);
      this.valuesChange.emit(next);
      return;
    }
    this.close(true);
    if (option.value !== this.value) {
      this.valueChange.emit(option.value);
    }
  }

  close(returnFocus = false): void {
    if (!this.isOpen()) return;
    this.isOpen.set(false);
    if (returnFocus) {
      this.host.nativeElement
        .querySelector<HTMLButtonElement>('.sbd-dd__trigger')
        ?.focus();
    }
  }

  onPanelKeydown(event: KeyboardEvent): void {
    const items = this.optionElements();
    const current = items.indexOf(document.activeElement as HTMLButtonElement);
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      const step = event.key === 'ArrowDown' ? 1 : -1;
      this.focusOption((current + step + items.length) % items.length);
    } else if (event.key === 'Home' || event.key === 'End') {
      event.preventDefault();
      this.focusOption(event.key === 'Home' ? 0 : items.length - 1);
    } else if (event.key === 'Escape') {
      event.preventDefault();
      this.close(true);
    } else if (event.key === 'Tab') {
      this.close();
    }
  }

  onTriggerKeydown(event: KeyboardEvent): void {
    if (
      (event.key === 'ArrowDown' || event.key === 'ArrowUp') &&
      !this.isOpen()
    ) {
      event.preventDefault();
      this.toggle();
    }
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (
      this.isOpen() &&
      !this.host.nativeElement.contains(event.target as Node)
    ) {
      this.close();
    }
  }

  private optionElements(): HTMLButtonElement[] {
    return Array.from(
      this.host.nativeElement.querySelectorAll<HTMLButtonElement>(
        '.sbd-dd__option',
      ),
    );
  }

  private focusOption(index: number): void {
    this.optionElements()[Math.max(0, index)]?.focus();
  }
}
